/**
 * Phase 7A + 7B + 7C FE — /forms/:refOrId — Form Designer canvas.
 *
 * Field editor (Phase 7A/7B AC1-AC7): palette to add fields, up/down
 * reorder, delete, right-side settings panel for label, variable name,
 * options, and validation rules (required, min/max, max_length,
 * custom error message).
 *
 * Phase 7C additions:
 *   - AC8 Design/Preview toggle — flips the canvas to a non-editable
 *     runtime render matching what data-entry users see.
 *   - AC9 Version History drawer — lists ctms_form_versions rows for
 *     this form, opens a read-only viewer for any prior version.
 *   - AC10 Publish confirmation modal — diffs the current draft
 *     against the last published version (added / removed / changed
 *     fields) before commit.
 *   - AC11 Save indicator — auto-save every 10s when dirty, header
 *     shows "Saved N min ago" / "Saving…" / "Unsaved changes".
 *
 * Drag-drop palette placement is the remaining UX upgrade that layers
 * on top.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowUp,
  ArrowDown,
  Check,
  Copy,
  Eye,
  FileEdit,
  GripVertical,
  History,
  Info,
  Loader2,
  Plus,
  Save,
  Send,
  Share2,
  Trash2,
  X,
  Zap,
  Blocks,
  GripHorizontal,
} from 'lucide-react';
import {
  ApiError,
  forms,
  procedures,
  schedule,
  fieldTemplates,
  studyDoaResponsibilities,
  adminSites,
  type ConditionExpr,
  type FieldTemplateRow,
  type FormFieldType,
  type FormStatus,
  type ProcedureRow,
  type VisitTemplateRow,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindedNotice } from '@/components/layout/BlindedNotice';
import { RuleBuilder } from '@/components/forms/RuleBuilder';
import { SingleSelectSearch, type SingleSelectOption } from '@/components/ui/SingleSelectSearch';
import {
  TemplateEditorModal,
  EMPTY_EDITOR,
  type EditorState,
} from '@/pages/admin/FieldTemplatesPage';
import { fmtDateTime } from '@/lib/dates';
import { errorText } from '@/lib/permissionError';
import { studyName } from '@/lib/studyLabel';

// ---------------------------------------------------------------------------
// Draft field shape — matches what publish.ts's FieldSchema expects.
// `config` carries all type-specific + Phase 7B rules.

interface DraftFieldConfig {
  required?: boolean;
  min?: number;
  max?: number;
  max_length?: number;
  error_message?: string;
  help_text?: string;
  options?: Array<{ value: string; label: string }>;
  visibility_rule?: ConditionExpr | null;
}

interface DraftField {
  field_key: string;
  field_label: string;
  field_type: FormFieldType;
  field_order: number;
  config: DraftFieldConfig;
}

const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & Time',
  select: 'Dropdown',
  radio: 'Radio',
  checkbox: 'Checkbox',
  textarea: 'Long text',
  calculated: 'Calculated',
};

const STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const STATUS_BADGE: Record<FormStatus, string> = {
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const CONFIGURE_FORM_ROLES = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'regulatory_specialist',
] as const;

// ---------------------------------------------------------------------------

// Bug found live (UAT: cloning from an already-open form leaves a stale
// clone dialog/canvas behind instead of showing the new form). React
// Router does NOT remount a route element when only its param changes —
// navigating from /forms/A to /forms/B via CloneModal's onSuccess reused
// this exact component instance, so `initialised` (guarding the field-
// canvas hydration effect below) and CloneModal's own `open` state both
// stayed stuck from form A. Keying the real page by refOrId forces a
// clean remount whenever the form identity actually changes.
export function FormDesignerPage() {
  const { refOrId } = useParams<{ refOrId: string }>();
  return <FormDesignerPageInner key={refOrId} />;
}

function FormDesignerPageInner() {
  const { refOrId } = useParams<{ refOrId: string }>();
  const canEdit = useHasRole(CONFIGURE_FORM_ROLES);
  const isPlatformAdmin = useHasRole(['platform_admin', 'application_admin']);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['form', 'detail', refOrId],
    queryFn: () => forms.detail(refOrId!),
    enabled: !!refOrId,
  });

  const form = detailQ.data?.form;
  const currentVersion = detailQ.data?.current_version;

  const [fields, setFields] = useState<DraftField[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [mode, setMode] = useState<'design' | 'preview'>('design');
  const [drawer, setDrawer] = useState<'versions' | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [savingNow, setSavingNow] = useState(false);
  const [lastPublishedFieldsJson, setLastPublishedFieldsJson] = useState<string | null>(
    null,
  );

  // Hydrate the canvas from draft_fields_json (falls back to the
  // published field defs if there's no draft yet). Also captures a
  // snapshot of the last-published field set so the Publish confirm
  // modal can compute a diff without a follow-up round-trip.
  useEffect(() => {
    if (initialised || !detailQ.data) return;
    const parsed = parseDraftJson(form?.draft_fields_json);
    const publishedFieldsFromDetail = detailQ.data.field_definitions.map((d, i) => ({
      field_key: d.field_key,
      field_label: d.field_label,
      field_type: d.field_type,
      field_order: d.field_order ?? i,
      config: safeParseConfig(d.config_json),
    }));
    if (parsed) {
      setFields(parsed);
    } else if (publishedFieldsFromDetail.length > 0) {
      setFields(publishedFieldsFromDetail);
    }
    if (publishedFieldsFromDetail.length > 0) {
      setLastPublishedFieldsJson(JSON.stringify(reindex(publishedFieldsFromDetail)));
    }
    setInitialised(true);
  }, [detailQ.data, form?.draft_fields_json, initialised]);

  const dupKeys = useMemo(() => findDuplicateKeys(fields), [fields]);

  const save = useMutation({
    mutationFn: (draft: DraftField[]) => {
      setSavingNow(true);
      return forms.updateDefinition(form!.form_id, {
        draft_fields_json: JSON.stringify(reindex(draft)),
      });
    },
    onSuccess: () => {
      setDirty(false);
      setLastSavedAt(new Date());
      setSavingNow(false);
    },
    onError: (e) => {
      setSavingNow(false);
      toast.error(errorText(e, 'Save failed'));
    },
  });

  // AC11 — auto-save every 10s when there are pending edits AND the
  // draft has no duplicate variable-name blockers. Manual Save Draft
  // remains available; auto-save is a safety-net for the header
  // indicator.
  const autoSave = useCallback(() => {
    if (!form) return;
    if (!dirty || save.isPending || dupKeys.size > 0) return;
    save.mutate(fields);
  }, [form, dirty, save, fields, dupKeys.size]);
  useAutoSaveEvery(autoSave, 10_000);

  const publish = useMutation({
    // Phase 7E AC16 (policy A) — visit_template_ids is required and non-empty.
    // The PublishConfirmModal below owns the multi-select and passes the
    // resolved list here. Empty means no publish — button stays disabled.
    mutationFn: (payload: { visitTemplateIds: string[]; requiredResponsibilityCode?: string; piSiOnly: boolean }) =>
      forms.publish(form!.form_id, {
        visit_template_ids: payload.visitTemplateIds,
        required_responsibility_code: payload.requiredResponsibilityCode,
        pi_si_only: payload.piSiOnly,
      }),
    onSuccess: (r) => {
      toast.success(`Published ${r.version_label} (${r.field_count} fields).`);
      setPublishOpen(false);
      setLastPublishedFieldsJson(JSON.stringify(reindex(fields)));
      qc.invalidateQueries({ queryKey: ['form', 'detail', refOrId] });
      qc.invalidateQueries({ queryKey: ['form', 'versions', refOrId] });
      qc.invalidateQueries({ queryKey: ['forms', 'list'] });
    },
    onError: (e) => toast.error(errorText(e, 'Publish failed')),
  });

  function addField(type: FormFieldType) {
    const nextIdx = fields.length;
    const key = mintUniqueKey(type, fields);
    setFields((prev) => [
      ...prev,
      {
        field_key: key,
        field_label: `New ${FIELD_TYPE_LABELS[type]} field`,
        field_type: type,
        field_order: nextIdx,
        config: type === 'select' || type === 'radio' || type === 'checkbox'
          ? { options: [{ value: 'opt1', label: 'Option 1' }] }
          : {},
      },
    ]);
    setSelectedIdx(nextIdx);
    setDirty(true);
  }

  function updateField(idx: number, next: DraftField) {
    setFields((prev) => prev.map((f, i) => (i === idx ? next : f)));
    setDirty(true);
  }

  function deleteField(idx: number) {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIdx(null);
    setDirty(true);
  }

  function moveField(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    setFields((prev) => {
      const copy = prev.slice();
      const tmp = copy[idx]!;
      copy[idx] = copy[target]!;
      copy[target] = tmp;
      return copy;
    });
    setSelectedIdx(target);
    setDirty(true);
  }

  if (detailQ.isLoading) {
    return (
      <div className="p-10 text-center text-sm text-ink-muted">
        <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading form…
      </div>
    );
  }
  if (detailQ.data?.blinded) {
    return (
      <div className="space-y-3">
        <PageHeader backTo="/forms" title="Form" />
        <BlindedNotice contentLabel="Form" roleMasterRef={detailQ.data.blinded_role_master_ref ?? null} />
      </div>
    );
  }
  if (detailQ.data?.esource_restricted) {
    return (
      <div className="space-y-3">
        <PageHeader backTo="/forms" title="Form" />
        <div className="card p-4 text-sm text-ink-secondary">
          eSource is not available to unblinded staff on this study.
        </div>
      </div>
    );
  }
  if (detailQ.isError || !form) {
    return (
      <div className="p-10 text-center text-sm text-danger-600">
        Failed to load form. {(detailQ.error as Error | undefined)?.message ?? ''}
      </div>
    );
  }

  /**
   * Feedback (Study details item 5) — a form already live on an active study
   * cannot be re-published until a protocol amendment flagging eSource forms
   * is approved. The draft stays editable on purpose: the amendment's review
   * checklist asks the designer to prepare exactly these changes while it is
   * in review. Only the act of making it live is held.
   */
  const amendmentBlocked = detailQ.data?.publish_requires_amendment === true;
  const canPublish =
    canEdit &&
    !dirty &&
    !save.isPending &&
    fields.length > 0 &&
    dupKeys.size === 0 &&
    !amendmentBlocked;

  return (
    <div className="space-y-3">
      <PageHeader
        backTo="/forms"
        title={form.name}
        subtitle={
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
            <span
              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[form.status]}`}
            >
              {STATUS_LABELS[form.status]}
            </span>
            {currentVersion && (
              <span>Version {currentVersion.version_label}</span>
            )}
          </p>
        }
      />
      <header className="flex flex-wrap items-start justify-end gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* AC11 — save indicator, always visible so the user knows
              the draft state at a glance. */}
          <SaveIndicator
            dirty={dirty}
            saving={savingNow || save.isPending}
            lastSavedAt={lastSavedAt}
          />

          {/* AC8 — Design ↔ Preview toggle. Not permission-gated: a
              read-only reviewer can still switch modes to look. */}
          <div className="inline-flex rounded border border-surface-border">
            <button
              type="button"
              onClick={() => setMode('design')}
              disabled={!canEdit}
              className={`rounded-l px-2 py-1.5 text-xs font-medium ${mode === 'design'
                ? 'bg-accent-600 text-white'
                : 'bg-surface-card text-ink-primary hover:bg-surface-muted/50'
                } disabled:opacity-50`}
            >
              <FileEdit className="mr-1 inline h-3 w-3" /> Design
            </button>
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={`rounded-r border-l border-surface-border px-2 py-1.5 text-xs font-medium ${mode === 'preview'
                ? 'bg-accent-600 text-white'
                : 'bg-surface-card text-ink-primary hover:bg-surface-muted/50'
                }`}
            >
              <Eye className="mr-1 inline h-3 w-3" /> Preview
            </button>
          </div>

          {/* AC9 — Version History drawer. */}
          <button
            type="button"
            onClick={() => setDrawer(drawer === 'versions' ? null : 'versions')}
            className={`rounded border border-surface-border px-2 py-1.5 text-xs font-medium hover:bg-surface-muted/50 ${drawer === 'versions' ? 'bg-surface-muted/50' : ''
              }`}
          >
            <History className="mr-1 inline h-3 w-3" /> Versions
          </button>

          {canEdit && (
            <>
              <CloneButton refOrId={refOrId!} currentName={form?.name ?? ''} navigate={navigate} />
              <CopyToSitesButton
                refOrId={refOrId!}
                isPlatformAdmin={isPlatformAdmin}
              />
              <button
                type="button"
                onClick={() => save.mutate(fields)}
                disabled={!dirty || save.isPending || dupKeys.size > 0}
                className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
              >
                <Save className="mr-1 inline h-3 w-3" />
                {save.isPending ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                onClick={() => setPublishOpen(true)}
                disabled={!canPublish || publish.isPending}
                title={
                  amendmentBlocked
                    ? 'A protocol amendment is required before this form can change'
                    : dirty
                      ? 'Save your changes first'
                      : dupKeys.size > 0
                        ? 'Fix duplicate variable names before publishing'
                        : fields.length === 0
                          ? 'Add at least one field to publish'
                          : 'Publish this form as the next version'
                }
                className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
              >
                <Send className="mr-1 inline h-3 w-3" />
                {publish.isPending ? 'Publishing…' : 'Publish to Study'}
              </button>
            </>
          )}
        </div>
      </header>

      {amendmentBlocked && (
        <div className="card border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="font-semibold">Protocol amendment required to change this form</div>
          <p className="mt-0.5">
            {detailQ.data?.publish_blocked_reason ??
              'This form is already live on an active study, so changing it is a protocol change.'}{' '}
            You can keep editing and saving the draft in the meantime — only publishing is
            held.
          </p>
          <Link
            to={`/studies/${form.study_id}`}
            className="mt-1.5 inline-block font-medium underline underline-offset-2"
          >
            Open the study's Protocol amendments panel
          </Link>
        </div>
      )}

      {dupKeys.size > 0 && (
        <div className="card border-danger-300 bg-danger-50 p-3 text-xs text-danger-800">
          <div className="font-semibold">Duplicate variable names</div>
          <p className="mt-0.5">
            Each Variable Name (Export) must be unique per form (Phase 7B AC5). Rename
            the duplicates before publishing:{' '}
            <span className="font-mono">{Array.from(dupKeys).join(', ')}</span>.
          </p>
        </div>
      )}

      {mode === 'preview' ? (
        <PreviewRuntime fields={fields} formName={form.name} />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[210px_1fr_320px]">
          {/* Field palette + procedure library (Phase 7D AC13) */}
          <aside className="card p-3 space-y-3">
            <div>
              <h2 className="mb-2 text-xs font-semibold text-ink-primary">Add field</h2>
              {canEdit ? (
                <div className="grid grid-cols-2 gap-1.5 lg:grid-cols-1">
                  {(Object.keys(FIELD_TYPE_LABELS) as FormFieldType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => addField(t)}
                      className="flex items-center gap-1.5 rounded border border-surface-border px-2 py-1.5 text-left text-xs hover:bg-surface-muted/50"
                    >
                      <Plus className="h-3 w-3 text-ink-muted" />
                      {FIELD_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-ink-muted">
                  You don't have permission to edit this form.
                </p>
              )}
            </div>

            {canEdit && (
              <ProcedurePalette
                studyId={form.study_id}
                onInsert={(fieldsToAdd) => {
                  setFields((prev) => {
                    const nextIdx = prev.length;
                    const used = new Set(prev.map((f) => f.field_key));
                    const withUniqueKeys = fieldsToAdd.map((f, i) => {
                      let key = f.field_key;
                      let counter = 1;
                      while (used.has(key)) {
                        key = `${f.field_key}_${counter++}`;
                      }
                      used.add(key);
                      return {
                        ...f,
                        field_key: key,
                        field_order: nextIdx + i,
                      };
                    });
                    return [...prev, ...withUniqueKeys];
                  });
                  setDirty(true);
                }}
              />
            )}

            {canEdit && (
              <FieldTemplatePalette
                onInsert={(fieldsToAdd) => {
                  const selectAt = fields.length;
                  setFields((prev) => {
                    const nextIdx = prev.length;
                    const used = new Set(prev.map((f) => f.field_key));
                    const withUniqueKeys = fieldsToAdd.map((f, i) => {
                      let key = f.field_key;
                      let counter = 1;
                      while (used.has(key)) {
                        key = `${f.field_key}_${counter++}`;
                      }
                      used.add(key);
                      return {
                        ...f,
                        field_key: key,
                        field_order: nextIdx + i,
                      };
                    });
                    return [...prev, ...withUniqueKeys];
                  });
                  setDirty(true);
                  setSelectedIdx(selectAt);
                }}
              />
            )}
          </aside>

          {/* Canvas — also acts as the drop target for the field-template palette. */}
          <div
            className="card p-3"
            onDragOver={(e) => {
              if (!canEdit) return;
              if (!e.dataTransfer.types.includes('application/x-field-template')) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={(e) => {
              if (!canEdit) return;
              const raw = e.dataTransfer.getData('application/x-field-template');
              if (!raw) return;
              e.preventDefault();
              try {
                const parsed = JSON.parse(raw) as DraftField[];
                if (!Array.isArray(parsed) || parsed.length === 0) return;
                setFields((prev) => {
                  const nextIdx = prev.length;
                  const used = new Set(prev.map((f) => f.field_key));
                  const withUniqueKeys = parsed.map((f, i) => {
                    let key = f.field_key;
                    let counter = 1;
                    while (used.has(key)) {
                      key = `${f.field_key}_${counter++}`;
                    }
                    used.add(key);
                    return { ...f, field_key: key, field_order: nextIdx + i };
                  });
                  return [...prev, ...withUniqueKeys];
                });
                setDirty(true);
              } catch {
                toast.error('Could not parse dropped template.');
              }
            }}
          >
            <h2 className="mb-2 text-xs font-semibold text-ink-primary">Fields</h2>
            {fields.length === 0 ? (
              <p className="rounded border border-dashed border-surface-border p-6 text-center text-xs text-ink-muted">
                No fields yet. Add one from the palette to start building the form.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {fields.map((f, i) => (
                  <li
                    key={i}
                    onClick={() => setSelectedIdx(i)}
                    className={`flex items-center gap-2 rounded border p-2 text-xs ${selectedIdx === i
                      ? 'border-accent-400 bg-accent-50/40'
                      : dupKeys.has(f.field_key)
                        ? 'border-danger-300 bg-danger-50/40'
                        : 'border-surface-border bg-surface-card'
                      } cursor-pointer hover:border-accent-300`}
                  >
                    <GripVertical className="h-3 w-3 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ink-primary">
                        {f.field_label || '(no label)'}
                        {f.config.required && <span className="ml-1 text-danger-600">*</span>}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[10px] text-ink-muted">
                        <span className="rounded bg-surface-muted/50 px-1 py-0.5">
                          {FIELD_TYPE_LABELS[f.field_type]}
                        </span>
                        <span className="font-mono">{f.field_key}</span>
                        {f.config.visibility_rule && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1 py-0.5 text-amber-700"
                            title="This field is shown only when its visibility conditions match."
                          >
                            <Zap className="h-2.5 w-2.5" /> Conditional
                          </span>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveField(i, -1);
                        }}
                        disabled={i === 0}
                        className="rounded p-1 text-ink-muted hover:bg-surface-muted/50 disabled:opacity-30"
                        title="Move up"
                      >
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveField(i, 1);
                        }}
                        disabled={i === fields.length - 1}
                        className="rounded p-1 text-ink-muted hover:bg-surface-muted/50 disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${f.field_label}"?`)) deleteField(i);
                        }}
                        className="rounded p-1 text-danger-600 hover:bg-danger-50"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Field settings */}
        <aside className="card p-3">
          <h2 className="mb-2 text-xs font-semibold text-ink-primary">
            Field settings
          </h2>
          {selectedIdx === null || !fields[selectedIdx] ? (
            <p className="text-[11px] text-ink-muted">
              Select a field on the canvas to edit its settings.
            </p>
          ) : (
            <FieldSettingsPanel
              field={fields[selectedIdx]}
              onChange={(next) => updateField(selectedIdx, next)}
              readOnly={!canEdit}
              allKeys={fields.map((x) => x.field_key)}
              allFields={fields}
              index={selectedIdx}
            />
          )}
        </aside>
      </div>
      )}

      {form.status === 'published' && mode === 'design' && (
        <div className="card p-3 text-[11px] text-ink-muted">
          <Info className="mr-1 inline h-3 w-3" />
          Data-entry users see the published version. Save draft to keep editing;
          publishing again creates a new version — prior responses stay bound to the
          version they were entered against.{' '}
          <Link to="/forms" className="text-accent-700 hover:underline">
            Back to list
          </Link>
        </div>
      )}

      {drawer === 'versions' && (
        <VersionsDrawer
          refOrId={refOrId!}
          formId={form.form_id}
          onClose={() => setDrawer(null)}
        />
      )}
      {publishOpen && (
        <PublishConfirmModal
          studyId={form.study_id}
          currentFields={fields}
          lastPublishedFieldsJson={lastPublishedFieldsJson}
          publishing={publish.isPending}
          onConfirm={(payload) => publish.mutate(payload)}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings panel — labels, variable name, help text, type-specific settings,
// and the Phase 7B validation rules block.

function FieldSettingsPanel({
  field,
  onChange,
  readOnly,
  allKeys,
  allFields,
  index,
}: {
  field: DraftField;
  onChange: (next: DraftField) => void;
  readOnly: boolean;
  allKeys: string[];
  allFields: DraftField[];
  index: number;
}) {
  const isNumeric = field.field_type === 'number' || field.field_type === 'calculated';
  const isTextish = field.field_type === 'text' || field.field_type === 'textarea';
  const isOptioned =
    field.field_type === 'select' ||
    field.field_type === 'radio' ||
    field.field_type === 'checkbox';

  const keyClash = allKeys.some((k, i) => i !== index && k === field.field_key);

  function setConfig(patch: Partial<DraftFieldConfig>) {
    onChange({ ...field, config: { ...field.config, ...patch } });
  }

  return (
    <div className="space-y-3">
      <label className="block text-[11px]">
        <span className="font-medium text-ink-secondary">Field label</span>
        <input
          type="text"
          value={field.field_label}
          onChange={(e) => onChange({ ...field, field_label: e.target.value })}
          disabled={readOnly}
          className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
        />
      </label>

      <label className="block text-[11px]">
        <span className="font-medium text-ink-secondary">
          Variable name (Export){' '}
          <span className="text-ink-muted">— unique per form</span>
        </span>
        <input
          type="text"
          value={field.field_key}
          onChange={(e) => onChange({ ...field, field_key: slugifyKey(e.target.value) })}
          disabled={readOnly}
          className={`mt-1 w-full rounded border px-2 py-1 font-mono text-xs disabled:opacity-70 ${keyClash ? 'border-danger-400 bg-danger-50/30' : 'border-surface-border'
            }`}
        />
        {keyClash && (
          <p className="mt-0.5 text-[10px] text-danger-700">
            Duplicate — another field on this form is using this variable name.
          </p>
        )}
      </label>

      <label className="block text-[11px]">
        <span className="font-medium text-ink-secondary">Help text</span>
        <input
          type="text"
          value={field.config.help_text ?? ''}
          onChange={(e) => setConfig({ help_text: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="Shown under the field at data entry"
          className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
        />
      </label>

      <div className="rounded border border-surface-border bg-surface-muted/20 p-2">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
          Validation rules
        </p>

        <label className="flex items-center gap-2 text-[11px]">
          <input
            type="checkbox"
            checked={!!field.config.required}
            onChange={(e) => setConfig({ required: e.target.checked || undefined })}
            disabled={readOnly || field.field_type === 'calculated'}
          />
          <span>Required</span>
        </label>
        {field.field_type === 'calculated' && (
          // Bug found live (UAT audit): "calculated" fields have no
          // compute engine anywhere in the app yet — nothing can ever
          // write a value into one. Marking one Required would
          // permanently block every submit of this form with no way
          // for a coordinator to resolve it.
          <p className="mt-1 text-[10px] text-ink-muted">
            Calculated fields can&rsquo;t be required yet — there&rsquo;s no way to populate one automatically.
          </p>
        )}

        {isNumeric && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="block text-[11px]">
              <span className="font-medium text-ink-secondary">Min</span>
              <input
                type="number"
                value={field.config.min ?? ''}
                onChange={(e) =>
                  setConfig({
                    min: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                disabled={readOnly}
                className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
              />
            </label>
            <label className="block text-[11px]">
              <span className="font-medium text-ink-secondary">Max</span>
              <input
                type="number"
                value={field.config.max ?? ''}
                onChange={(e) =>
                  setConfig({
                    max: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
                disabled={readOnly}
                className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
              />
            </label>
          </div>
        )}

        {isTextish && (
          <label className="mt-2 block text-[11px]">
            <span className="font-medium text-ink-secondary">Max length</span>
            <input
              type="number"
              min={1}
              value={field.config.max_length ?? ''}
              onChange={(e) =>
                setConfig({
                  max_length: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              disabled={readOnly}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
            />
          </label>
        )}

        <label className="mt-2 block text-[11px]">
          <span className="font-medium text-ink-secondary">Custom error message</span>
          <input
            type="text"
            value={field.config.error_message ?? ''}
            onChange={(e) => setConfig({ error_message: e.target.value || undefined })}
            disabled={readOnly}
            placeholder="Overrides the default when any rule fails"
            className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-70"
          />
        </label>
      </div>

      {isOptioned && (
        <div className="rounded border border-surface-border bg-surface-muted/20 p-2">
          <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            <span>Options</span>
            {!readOnly && (
              <button
                type="button"
                onClick={() =>
                  setConfig({
                    options: [
                      ...(field.config.options ?? []),
                      {
                        value: `opt${(field.config.options?.length ?? 0) + 1}`,
                        label: `Option ${(field.config.options?.length ?? 0) + 1}`,
                      },
                    ],
                  })
                }
                className="rounded border border-surface-border px-1.5 py-0.5 text-[10px] font-normal text-ink-primary hover:bg-surface-muted/50"
              >
                <Plus className="mr-0.5 inline h-2.5 w-2.5" /> Add
              </button>
            )}
          </div>
          <div className="space-y-1">
            {(field.config.options ?? []).map((opt, i) => (
              <div key={i} className="flex items-center gap-1">
                <input
                  type="text"
                  value={opt.value}
                  onChange={(e) => {
                    const next = (field.config.options ?? []).slice();
                    next[i] = { ...opt, value: slugifyKey(e.target.value) };
                    setConfig({ options: next });
                  }}
                  disabled={readOnly}
                  placeholder="value"
                  className="w-24 rounded border border-surface-border px-1.5 py-0.5 font-mono text-[10px] disabled:opacity-70"
                />
                <input
                  type="text"
                  value={opt.label}
                  onChange={(e) => {
                    const next = (field.config.options ?? []).slice();
                    next[i] = { ...opt, label: e.target.value };
                    setConfig({ options: next });
                  }}
                  disabled={readOnly}
                  placeholder="label"
                  className="min-w-0 flex-1 rounded border border-surface-border px-1.5 py-0.5 text-[10px] disabled:opacity-70"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = (field.config.options ?? []).filter((_, k) => k !== i);
                      setConfig({ options: next });
                    }}
                    className="rounded p-0.5 text-danger-600 hover:bg-danger-50"
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded border border-surface-border bg-surface-muted/20 p-2">
        <div className="mb-1.5 flex items-center justify-between">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
            Visibility
          </p>
          <label className="flex items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              checked={!!field.config.visibility_rule}
              onChange={(e) => {
                if (e.target.checked) {
                  setConfig({ visibility_rule: { op: 'eq', field: 'subject.sex', value: '' } });
                } else {
                  setConfig({ visibility_rule: null });
                }
              }}
              disabled={readOnly}
            />
            <span>Show only when…</span>
          </label>
        </div>
        {field.config.visibility_rule ? (
          <RuleBuilder
            value={field.config.visibility_rule}
            onChange={(next) => setConfig({ visibility_rule: next })}
            siblingFields={allFields
              .filter((f) => f.field_key !== field.field_key)
              .map((f) => ({
                field_key: f.field_key,
                field_label: f.field_label,
                field_type: f.field_type,
                options: f.config.options,
              }))}
          />
        ) : (
          <p className="text-[10px] text-ink-muted">
            Field is always visible. Toggle "Show only when…" to gate it on subject, study,
            or another field's value.
          </p>
        )}
      </div>

      <div className="rounded border border-surface-border bg-surface-muted/10 p-2 text-[10px] text-ink-muted">
        <Check className="mr-0.5 inline h-3 w-3 text-emerald-600" />
        Rules save with the form and re-run on the data-entry surface. Publishing
        freezes them onto the new version.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers.

function parseDraftJson(raw: string | null | undefined): DraftField[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((entry: unknown, i: number): DraftField => {
      const obj = (entry as Partial<DraftField>) ?? {};
      return {
        field_key: obj.field_key ?? `field_${i}`,
        field_label: obj.field_label ?? '',
        field_type: (obj.field_type ?? 'text') as FormFieldType,
        field_order: obj.field_order ?? i,
        config: (obj.config ?? {}) as DraftFieldConfig,
      };
    });
  } catch {
    return null;
  }
}

function safeParseConfig(raw: string | null | undefined): DraftFieldConfig {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function slugifyKey(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

function mintUniqueKey(type: FormFieldType, existing: DraftField[]): string {
  const base = type;
  const used = new Set(existing.map((f) => f.field_key));
  for (let i = 1; i < 1000; i++) {
    const candidate = `${base}_${i}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

function reindex(fields: DraftField[]): DraftField[] {
  return fields.map((f, i) => ({ ...f, field_order: i }));
}

function findDuplicateKeys(fields: DraftField[]): Set<string> {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const f of fields) {
    if (seen.has(f.field_key)) dup.add(f.field_key);
    seen.add(f.field_key);
  }
  return dup;
}

// ---------------------------------------------------------------------------
// AC11 — Save indicator + auto-save hook.

/**
 * Fires `cb` every `intervalMs`. `cb` should short-circuit itself when
 * there's nothing to do — the hook doesn't inspect state, it just ticks.
 * Uses `useRef` so the interval doesn't rebind on every parent render.
 */
function useAutoSaveEvery(cb: () => void, intervalMs: number) {
  const cbRef = useRef(cb);
  useEffect(() => {
    cbRef.current = cb;
  }, [cb]);
  useEffect(() => {
    const id = window.setInterval(() => cbRef.current(), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
}

function SaveIndicator({
  dirty,
  saving,
  lastSavedAt,
}: {
  dirty: boolean;
  saving: boolean;
  lastSavedAt: Date | null;
}) {
  // Tick every 30s so "Saved 2m ago" stays fresh without hammering
  // re-renders.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  let label: string;
  let tone: string;
  if (saving) {
    label = 'Saving…';
    tone = 'text-ink-muted';
  } else if (dirty) {
    label = 'Unsaved changes';
    tone = 'text-amber-700';
  } else if (lastSavedAt) {
    label = `Saved ${relativeAgo(lastSavedAt)}`;
    tone = 'text-emerald-700';
  } else {
    label = 'All changes saved';
    tone = 'text-ink-muted';
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-[11px] font-medium ${tone}`}
      title={
        lastSavedAt
          ? `Last saved ${lastSavedAt.toLocaleString()}`
          : 'No save yet this session'
      }
    >
      {saving ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : dirty ? (
        <Save className="h-3 w-3" />
      ) : (
        <Check className="h-3 w-3" />
      )}
      {label}
    </span>
  );
}

function relativeAgo(when: Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// AC8 — Preview runtime. Renders the current fields as they'd appear
// at data-entry, non-editable. Shares the field-shape contract with
// `VisitFormsSection.FieldInput` — kept inline here so the designer
// doesn't drag in the visit page's data-entry wiring.

function PreviewRuntime({
  fields,
  formName,
}: {
  fields: DraftField[];
  formName: string;
}) {
  if (fields.length === 0) {
    return (
      <div className="card p-8 text-center text-xs text-ink-muted">
        No fields to preview. Switch to Design to add some.
      </div>
    );
  }
  return (
    <div className="card p-4">
      <div className="mb-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Preview — how data-entry users see this form
        </div>
        <h2 className="text-sm font-semibold text-ink-primary">{formName}</h2>
      </div>
      <div className="space-y-3">
        {fields.map((f, i) => (
          <PreviewFieldRow key={i} field={f} />
        ))}
      </div>
      <p className="mt-4 border-t border-surface-border pt-3 text-[11px] text-ink-muted">
        This is a live render — validation rules will fire when the form is used.
        Data cannot be entered from Preview.
      </p>
    </div>
  );
}

function PreviewFieldRow({ field }: { field: DraftField }) {
  const options = field.config.options ?? [];
  return (
    <div className="rounded border border-surface-border bg-surface-card p-3">
      <label className="block text-xs">
        <span className="font-medium text-ink-secondary">
          {field.field_label}
          {field.config.required && <span className="ml-1 text-danger-600">*</span>}
        </span>
        {field.config.help_text && (
          <span className="mt-0.5 block text-[10px] text-ink-muted">
            {field.config.help_text}
          </span>
        )}
        <div className="mt-1">
          {(() => {
            switch (field.field_type) {
              case 'number':
              case 'calculated':
                return (
                  <input
                    type="number"
                    disabled
                    placeholder={
                      field.config.min != null || field.config.max != null
                        ? `${field.config.min ?? '−∞'} – ${field.config.max ?? '∞'}`
                        : ''
                    }
                    className="w-full rounded border border-surface-border px-3 py-1.5 text-sm opacity-70"
                  />
                );
              case 'date':
                return (
                  <input
                    type="date"
                    disabled
                    className="w-full rounded border border-surface-border px-3 py-1.5 text-sm opacity-70"
                  />
                );
              case 'datetime':
                return (
                  <input
                    type="datetime-local"
                    disabled
                    className="w-full rounded border border-surface-border px-3 py-1.5 text-sm opacity-70"
                  />
                );
              case 'textarea':
                return (
                  <textarea
                    disabled
                    rows={3}
                    className="w-full rounded border border-surface-border px-3 py-2 text-sm opacity-70"
                  />
                );
              case 'select':
              case 'radio':
                return (
                  <select
                    disabled
                    className="w-full rounded border border-surface-border px-3 py-1.5 text-sm opacity-70"
                  >
                    <option value="">— Select —</option>
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                );
              case 'checkbox':
                return (
                  <div className="space-y-1">
                    {options.map((o) => (
                      <label key={o.value} className="flex items-center gap-2 text-xs">
                        <input type="checkbox" disabled />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                );
              case 'text':
              default:
                return (
                  <input
                    type="text"
                    disabled
                    className="w-full rounded border border-surface-border px-3 py-1.5 text-sm opacity-70"
                  />
                );
            }
          })()}
        </div>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AC9 — Version History drawer.

function VersionsDrawer({
  refOrId,
  formId,
  onClose,
}: {
  refOrId: string;
  formId: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['form', 'versions', refOrId],
    queryFn: () => forms.versions(refOrId),
  });
  const [openVersionId, setOpenVersionId] = useState<string | null>(null);

  return (
    <div className="fixed inset-y-0 right-0 z-[90] flex w-full max-w-md flex-col border-l border-surface-border bg-surface-card shadow-xl">
      <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">
            <History className="mr-1 inline h-4 w-4 text-brand-500" />
            Version history
          </h3>
          <p className="text-[11px] text-ink-muted">{q.data?.count ?? 0} versions</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {q.isLoading ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading versions…
          </div>
        ) : q.isError ? (
          <div className="p-6 text-center text-xs text-danger-600">
            Failed to load version history.
          </div>
        ) : (q.data?.rows ?? []).length === 0 ? (
          <p className="p-6 text-center text-xs text-ink-muted">
            No published versions yet — publish this form to record its first version.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {q.data!.rows.map((v) => (
              <li
                key={v.form_version_id}
                className={`rounded border p-2 text-xs ${v.is_current
                  ? 'border-emerald-300 bg-emerald-50/40'
                  : 'border-surface-border bg-surface-card'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-ink-primary">
                      {v.version_label}{' '}
                      <span className="text-[10px] text-ink-muted">
                        · v{v.version_number}
                      </span>
                      {v.is_current && (
                        <span className="ml-2 rounded border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-[9px] font-semibold text-emerald-700">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[10px] text-ink-muted">
                      Published {fmtDateTime(v.published_at)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setOpenVersionId(
                        openVersionId === v.form_version_id ? null : v.form_version_id,
                      )
                    }
                    className="rounded border border-surface-border px-2 py-0.5 text-[10px] font-medium hover:bg-surface-muted/50"
                  >
                    {openVersionId === v.form_version_id ? 'Hide' : 'View'}
                  </button>
                </div>
                {openVersionId === v.form_version_id && (
                  <VersionViewer formId={formId} versionId={v.form_version_id} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function VersionViewer({
  formId,
  versionId,
}: {
  formId: string;
  versionId: string;
}) {
  const q = useQuery({
    queryKey: ['form', 'version-detail', formId, versionId],
    queryFn: () => forms.versionDetail(formId, versionId),
  });
  if (q.isLoading) {
    return (
      <div className="mt-2 rounded border border-surface-border bg-surface-muted/20 p-2 text-center text-[10px] text-ink-muted">
        <Loader2 className="mx-auto h-3 w-3 animate-spin" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="mt-2 rounded border border-danger-300 bg-danger-50 p-2 text-[10px] text-danger-700">
        Failed to load version.
      </div>
    );
  }
  const defs = q.data.field_definitions;
  return (
    <div className="mt-2 rounded border border-surface-border bg-surface-muted/20 p-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
        {defs.length} field{defs.length === 1 ? '' : 's'}
      </div>
      <ul className="mt-1 space-y-0.5 text-[10px]">
        {defs
          .slice()
          .sort((a, b) => a.field_order - b.field_order)
          .map((d) => (
            <li key={d.field_id} className="flex items-center gap-1.5">
              <span className="rounded bg-surface-muted/50 px-1 py-0.5">
                {d.field_type}
              </span>
              <span className="font-medium text-ink-primary">{d.field_label}</span>
              <span className="font-mono text-ink-muted">{d.field_key}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AC10 — Publish confirmation modal with draft-vs-last-published diff.

function PublishConfirmModal({
  studyId,
  currentFields,
  lastPublishedFieldsJson,
  publishing,
  onConfirm,
  onClose,
}: {
  studyId: string;
  currentFields: DraftField[];
  lastPublishedFieldsJson: string | null;
  publishing: boolean;
  onConfirm: (payload: { visitTemplateIds: string[]; requiredResponsibilityCode?: string; piSiOnly: boolean }) => void;
  onClose: () => void;
}) {
  const diff = useMemo(
    () => diffFields(lastPublishedFieldsJson, currentFields),
    [lastPublishedFieldsJson, currentFields],
  );
  const isFirstPublish = !lastPublishedFieldsJson;

  // Phase 7E AC16 (policy A) — load the study's visit templates so the
  // designer can bind the form to specific visits at publish time.
  // Publish stays disabled until ≥ 1 template is picked; forms with no
  // binding aren't allowed to become 'published' at all (they'd be
  // invisible to data entry) — the BE rejects such a publish with 400.
  const scheduleQ = useQuery({
    queryKey: ['study', studyId, 'schedule'],
    queryFn: () => schedule.get(studyId),
    enabled: !!studyId,
  });
  const visitTemplates: VisitTemplateRow[] = useMemo(() => {
    const rows = scheduleQ.data?.visits ?? [];
    return [...rows].sort((a, b) => (a.visit_order ?? 0) - (b.visit_order ?? 0));
  }, [scheduleQ.data]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const allSelected = visitTemplates.length > 0 && selectedIds.size === visitTemplates.length;
  const canPublish = selectedIds.size >= 1 && !publishing;

  // Form governance (Jawahar 2026-09-22) — bind the form to a DoA
  // responsibility (only delegated users may fill) and/or restrict it to
  // PI/SI. Delegation of Authority is the source of truth for eSource.
  const [requiredResponsibilityCode, setRequiredResponsibilityCode] = useState<string>('');
  const [piSiOnly, setPiSiOnly] = useState<boolean>(false);
  const respQ = useQuery({
    queryKey: ['study', studyId, 'doa-responsibilities'],
    queryFn: () => studyDoaResponsibilities.get(studyId),
    enabled: !!studyId,
  });
  const responsibilities = respQ.data?.catalog ?? [];

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(allSelected ? new Set() : new Set(visitTemplates.map((v) => v.template_visit_id)));
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">
              Publish to Study
            </h3>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {isFirstPublish
                ? 'First publish — this creates version v1 for data entry.'
                : 'Creates the next version; existing form instances stay bound to their version.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-xs">
          {isFirstPublish ? (
            <p className="rounded border border-emerald-300 bg-emerald-50 p-2 text-[11px] text-emerald-800">
              {currentFields.length} field{currentFields.length === 1 ? '' : 's'} will be included.
            </p>
          ) : (
            <>
              <PublishDiffSection
                title="Added"
                items={diff.added.map((f) => `${f.field_label} (${f.field_key})`)}
                tone="emerald"
              />
              <PublishDiffSection
                title="Removed"
                items={diff.removed.map((f) => `${f.field_label} (${f.field_key})`)}
                tone="danger"
              />
              <PublishDiffSection
                title="Changed"
                items={diff.changed.map(
                  (c) => `${c.field_label} (${c.field_key}) — ${c.summary}`,
                )}
                tone="amber"
              />
              {diff.added.length === 0 &&
                diff.removed.length === 0 &&
                diff.changed.length === 0 && (
                  <p className="rounded border border-surface-border bg-surface-muted/20 p-2 text-[11px] text-ink-muted">
                    No changes vs the current published version — publishing anyway will
                    still stamp a new version row (empty diff).
                  </p>
                )}
            </>
          )}

          {/* Phase 7E AC16 — visit binding picker. */}
          <div className="rounded border border-surface-border bg-surface-muted/10 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold text-ink-primary">
                  Applies to visits <span className="text-danger-600">*</span>
                </div>
                <div className="text-[10px] text-ink-muted">
                  Forms without ≥ 1 visit stay in draft — they’re invisible to data entry.
                </div>
              </div>
              {visitTemplates.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-[10px] font-medium text-accent-700 hover:underline"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
            {scheduleQ.isLoading ? (
              <div className="flex items-center gap-1.5 text-[11px] text-ink-muted">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading visit schedule…
              </div>
            ) : visitTemplates.length === 0 ? (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-800">
                No visit templates defined for this study yet. Add visits on the
                study schedule first — this form can’t publish until at least one
                visit exists to bind to.
              </div>
            ) : (
              <ul className="space-y-1">
                {visitTemplates.map((v) => (
                  <li key={v.template_visit_id}>
                    <label className="flex items-start gap-2 rounded px-1 py-1 hover:bg-surface-muted/30">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(v.template_visit_id)}
                        onChange={() => toggle(v.template_visit_id)}
                        className="mt-0.5 h-3 w-3"
                      />
                      <span className="flex-1 text-[11px]">
                        <span className="font-medium text-ink-primary">{v.visit_name}</span>
                        {typeof v.visit_order === 'number' && (
                          <span className="ml-1 text-ink-muted">
                            (order {v.visit_order}
                            {typeof v.nominal_day === 'number' ? `, day ${v.nominal_day}` : ''}
                            )
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-surface-border p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              Governance (Delegation of Authority is the source of truth)
            </p>
            <label className="block text-[12px] text-ink-secondary">
              Required delegation to fill this form
              <select
                value={requiredResponsibilityCode}
                onChange={(e) => setRequiredResponsibilityCode(e.target.value)}
                className="mt-1 w-full rounded border border-surface-border bg-surface-subtle px-2 py-1.5 text-[13px] text-ink-primary"
              >
                <option value="">No restriction — any delegated study member</option>
                {responsibilities.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
              <span className="mt-1 block text-[10px] text-ink-muted">
                Only users whose approved delegation includes this responsibility can see and fill the form.
              </span>
            </label>
            <label className="mt-3 flex items-start gap-2 text-[12px] text-ink-secondary">
              <input
                type="checkbox"
                checked={piSiOnly}
                onChange={(e) => setPiSiOnly(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <strong>PI / Sub-Investigator only</strong> — coordinators and other roles cannot fill this form.
              </span>
            </label>
          </div>
        </div>
        <footer className="flex items-center justify-between gap-2 border-t border-surface-border px-4 py-3">
          <div className="text-[10px] text-ink-muted">
            {selectedIds.size === 0
              ? 'Pick at least one visit to enable Publish.'
              : `${selectedIds.size} visit${selectedIds.size === 1 ? '' : 's'} selected.`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm({
                visitTemplateIds: Array.from(selectedIds),
                requiredResponsibilityCode: requiredResponsibilityCode || undefined,
                piSiOnly,
              })}
              disabled={!canPublish}
              title={
                selectedIds.size === 0
                  ? 'Pick at least one visit before publishing'
                  : 'Publish this version bound to the selected visits'
              }
              className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="mr-1 inline h-3 w-3" />
              {publishing ? 'Publishing…' : 'Publish to Study'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function PublishDiffSection({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: 'emerald' | 'danger' | 'amber';
}) {
  if (items.length === 0) return null;
  const borderTone =
    tone === 'emerald'
      ? 'border-emerald-300 bg-emerald-50'
      : tone === 'danger'
        ? 'border-danger-300 bg-danger-50'
        : 'border-amber-300 bg-amber-50';
  const textTone =
    tone === 'emerald'
      ? 'text-emerald-800'
      : tone === 'danger'
        ? 'text-danger-800'
        : 'text-amber-800';
  return (
    <div className={`rounded border p-2 ${borderTone}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${textTone}`}>
        {title} · {items.length}
      </div>
      <ul className={`mt-1 space-y-0.5 text-[11px] ${textTone}`}>
        {items.map((it, i) => (
          <li key={i}>• {it}</li>
        ))}
      </ul>
    </div>
  );
}

interface FieldDiff {
  added: DraftField[];
  removed: DraftField[];
  changed: Array<{ field_key: string; field_label: string; summary: string }>;
}

// ---------------------------------------------------------------------------
// AC17 — Clone Design action. Clones the current form on its own study
// and navigates to the fresh draft.

function CloneButton({
  refOrId,
  currentName,
  navigate,
}: {
  refOrId: string;
  currentName: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Clone this design — creates an independently editable copy on this study"
        className="rounded border border-surface-border px-2 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
      >
        <Copy className="mr-1 inline h-3 w-3" />
        Clone
      </button>
      {open && (
        <CloneModal refOrId={refOrId} currentName={currentName} navigate={navigate} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

// Bug found live (UAT: "Clone Forms: We cannot set a new name to the
// cloned form.") — the backend already accepts a `name` override
// (defaults to "<name> (Clone)" if omitted); this button just never
// asked for one. Small modal instead of a bare `prompt()` to match the
// rest of this page's dialog conventions (see CopyToSitesModal below).
function CloneModal({
  refOrId,
  currentName,
  navigate,
  onClose,
}: {
  refOrId: string;
  currentName: string;
  navigate: ReturnType<typeof useNavigate>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(currentName ? `${currentName} (Clone)` : '');

  const clone = useMutation({
    mutationFn: () => forms.clone(refOrId, { name: name.trim() || undefined }),
    onSuccess: (r) => {
      toast.success(`Cloned as ${r.reference_number}.`);
      qc.invalidateQueries({ queryKey: ['forms', 'list'] });
      onClose();
      navigate(`/forms/${encodeURIComponent(r.reference_number)}`);
    },
    onError: (e) => toast.error(errorText(e, 'Clone failed')),
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-sm rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink-primary">Clone this design</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="px-4 py-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Name for the clone</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={currentName ? `${currentName} (Clone)` : 'Untitled (Clone)'}
              className="mt-1 w-full rounded border border-surface-border px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => clone.mutate()}
            disabled={clone.isPending}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {clone.isPending ? 'Cloning…' : 'Clone'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AC18 — Copy to Sites (platform-admin only).

function CopyToSitesButton({
  refOrId,
  isPlatformAdmin,
}: {
  refOrId: string;
  isPlatformAdmin: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!isPlatformAdmin) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Copy this design onto studies at another site"
        className="rounded border border-surface-border px-2 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
      >
        <Share2 className="mr-1 inline h-3 w-3" />
        Copy to sites
      </button>
      {open && (
        <CopyToSitesModal refOrId={refOrId} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

// Bug found live (UAT: "Copy form design to sites is looking for Study to
// be available in the target sites, this should be independent of
// study"). Redesigned as a two-step flow: pick ONE target site, then an
// explicit multi-select of that site's studies (with "All") — no more
// auto-matching by protocol_number, which silently skipped every site
// that didn't happen to run the exact same protocol.
function CopyToSitesModal({
  refOrId,
  onClose,
}: {
  refOrId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedStudyIds, setSelectedStudyIds] = useState<Set<string>>(new Set());

  const sitesQ = useQuery({
    queryKey: ['admin', 'sites', 'list'],
    queryFn: () => adminSites.list(),
    staleTime: 60_000,
  });
  const sites = (sitesQ.data?.sites ?? [])
    .filter((s) => s.status === 'active')
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
  const siteOptions: SingleSelectOption[] = sites.map((s) => ({
    value: s.site_id,
    label: s.name,
  }));

  const studiesQ = useQuery({
    queryKey: ['forms', refOrId, 'copy-to-sites', 'studies', selectedSiteId],
    queryFn: () => forms.listCopyToSitesStudies(refOrId, selectedSiteId!),
    enabled: !!selectedSiteId,
  });
  const studies = studiesQ.data?.studies ?? [];
  const allSelected = studies.length > 0 && selectedStudyIds.size === studies.length;

  function pickSite(siteId: string) {
    setSelectedSiteId(siteId);
    setSelectedStudyIds(new Set());
    run.reset();
  }

  function toggleStudy(studyId: string) {
    setSelectedStudyIds((prev) => {
      const next = new Set(prev);
      if (next.has(studyId)) next.delete(studyId);
      else next.add(studyId);
      return next;
    });
  }

  function toggleAll() {
    setSelectedStudyIds(allSelected ? new Set() : new Set(studies.map((s) => s.study_id)));
  }

  const run = useMutation({
    mutationFn: () =>
      forms.copyToSites(refOrId, { target_study_ids: Array.from(selectedStudyIds) }),
    onSuccess: (r) => {
      toast.success(`Copied ${r.copied_count} / ${r.total_targets} target studies.`);
      qc.invalidateQueries({ queryKey: ['forms', 'list'] });
    },
    onError: (e) => toast.error(errorText(e, 'Copy failed')),
  });

  const resultByStudyId = new Map<string, { status: string; reason: string | null; reference_number: string | null }>();
  if (run.data) {
    for (const r of run.data.results) {
      resultByStudyId.set(r.study_id, {
        status: r.status,
        reason: r.reason ?? null,
        reference_number: r.reference_number ?? null,
      });
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-y-auto rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">Copy design to sites</h3>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Platform-admin only. Pick a site, then choose which of its studies get the copy.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="px-4 py-3 text-xs">
          <label className="block">
            <span className="font-medium text-ink-secondary">1. Target site</span>
            <div className="mt-1">
              <SingleSelectSearch
                options={siteOptions}
                value={selectedSiteId}
                onChange={(v) => (v ? pickSite(v) : undefined)}
                placeholder={sitesQ.isLoading ? 'Loading sites…' : 'Search sites…'}
                disabled={sitesQ.isLoading}
              />
            </div>
          </label>

          {selectedSiteId && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-ink-secondary">2. Target studies</span>
                {studies.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-ink-secondary">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    All studies at this site
                  </label>
                )}
              </div>
              {studiesQ.isLoading ? (
                <div className="mt-2 flex items-center justify-center gap-2 py-8 text-[11px] text-ink-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading studies…
                </div>
              ) : studies.length === 0 ? (
                <p className="mt-2 py-6 text-center text-[11px] text-ink-muted">
                  This site has no other studies to copy onto.
                </p>
              ) : (
                <ul className="mt-2 max-h-64 divide-y divide-surface-border overflow-y-auto rounded border border-surface-border">
                  {studies.map((s) => {
                    const checked = selectedStudyIds.has(s.study_id);
                    const result = resultByStudyId.get(s.study_id);
                    return (
                      <li key={s.study_id} className="flex items-center gap-2 px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStudy(s.study_id)}
                          id={`study-${s.study_id}`}
                        />
                        <label htmlFor={`study-${s.study_id}`} className="min-w-0 flex-1 cursor-pointer">
                          <div className="truncate text-[12px] text-ink-primary">{studyName(s)}</div>
                          <div className="truncate font-mono text-[10px] text-ink-muted">{s.protocol_number}</div>
                        </label>
                        {result && (
                          <span
                            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                              result.status === 'copied'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                            title={result.reason ?? ''}
                          >
                            {result.status === 'copied' && result.reference_number
                              ? `Copied · ${result.reference_number}`
                              : result.status.replace(/^skipped_/, 'skipped · ').replace(/_/g, ' ')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {run.data && (
                <p className="mt-2 text-[11px] text-ink-muted">
                  Ineligible or duplicate rows are shown as skipped — hover the badge for the reason.
                </p>
              )}
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={selectedStudyIds.size === 0 || run.isPending}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {run.isPending
              ? 'Copying…'
              : `Copy to ${selectedStudyIds.size} stud${selectedStudyIds.size === 1 ? 'y' : 'ies'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AC13 — Procedure Library palette section.

function ProcedurePalette({
  studyId,
  onInsert,
}: {
  studyId: string;
  onInsert: (fields: DraftField[]) => void;
}) {
  const q = useQuery({
    queryKey: ['procedures', 'palette', studyId],
    queryFn: () => procedures.list({ study_id: studyId, active_only: true }),
    staleTime: 60_000,
  });
  const rows = q.data?.rows ?? [];
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold text-ink-primary">Procedure library</h2>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-[10px] text-ink-muted hover:text-ink-primary"
        >
          {expanded ? 'Hide' : 'Show'}
        </button>
      </div>
      {!expanded ? null : q.isLoading ? (
        <div className="text-[10px] text-ink-muted">
          <Loader2 className="mr-1 inline h-2.5 w-2.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-[10px] text-ink-muted">
          No procedures visible for this study. Create one via the procedure catalog.
        </p>
      ) : (
        <ul className="space-y-1">
          {rows.map((p) => (
            <ProcedurePaletteRow
              key={p.procedure_id}
              procedure={p}
              onInsert={onInsert}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProcedurePaletteRow({
  procedure,
  onInsert,
}: {
  procedure: ProcedureRow;
  onInsert: (fields: DraftField[]) => void;
}) {
  const [count, previewFields] = useMemo(() => {
    try {
      const parsed = JSON.parse(procedure.current_fields_json) as DraftField[];
      return [parsed.length, parsed];
    } catch {
      return [0, [] as DraftField[]];
    }
  }, [procedure.current_fields_json]);

  return (
    <li className="rounded border border-surface-border bg-surface-card p-1.5">
      <div className="flex items-center justify-between gap-1">
        <div className="min-w-0">
          <div className="truncate text-[11px] font-medium text-ink-primary">
            {procedure.label}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-[9px] text-ink-muted">
            <span className="rounded bg-surface-muted/50 px-1">
              {procedure.scope}
            </span>
            <span className="font-mono">{procedure.code}</span>
            <span>· {count} field{count === 1 ? '' : 's'}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onInsert(previewFields)}
          disabled={count === 0}
          title={count === 0 ? 'Procedure has no published version yet' : 'Add procedure fields to the canvas'}
          className="shrink-0 rounded border border-accent-300 bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50"
        >
          Insert
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Field-Template palette — tenant-scoped reusable field-groups seeded
// via /admin/field-templates. Click a row (or drag it onto the canvas
// area) to append its fields to the current form's draft.

function FieldTemplatePalette({
  onInsert,
}: {
  onInsert: (fields: DraftField[]) => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['field-templates', 'palette'],
    queryFn: () => fieldTemplates.list({ activeOnly: true }),
    staleTime: 5_000,
  });
  const [expanded, setExpanded] = useState(true);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const rows = q.data?.templates ?? [];

  const grouped = useMemo(() => {
    const buckets = new Map<string, FieldTemplateRow[]>();
    for (const t of rows) {
      const key = t.category?.trim() || 'Uncategorised';
      const arr = buckets.get(key) ?? [];
      arr.push(t);
      buckets.set(key, arr);
    }
    return Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-1 text-xs font-semibold text-ink-primary">
          <Blocks className="h-3 w-3 text-brand-500" />
          Field templates
        </h2>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-[10px] text-ink-muted hover:text-ink-primary"
        >
          {expanded ? 'Hide' : 'Show'}
        </button>
      </div>
      {expanded && (
        <>
          {/* Inline "+ New template" — creates a template without leaving the
              designer, then it appears in the palette below on save. */}
          <button
            type="button"
            onClick={() => setEditor({ ...EMPTY_EDITOR })}
            className="mb-2 flex w-full items-center justify-center gap-1 rounded border border-dashed border-accent-300 bg-accent-50/40 px-2 py-1.5 text-[11px] font-medium text-accent-700 hover:bg-accent-50"
            title="Create a new reusable field-group without leaving the designer"
          >
            <Plus className="h-3 w-3" /> New template
          </button>

          {q.isLoading ? (
            <div className="text-[10px] text-ink-muted">
              <Loader2 className="mr-1 inline h-2.5 w-2.5 animate-spin" /> Loading…
            </div>
          ) : q.isError ? (
            <div className="rounded border border-danger-300 bg-danger-50 p-2 text-[10px] text-danger-800">
              <strong>Couldn't load templates.</strong>
              <div className="mt-0.5">
                {q.error instanceof ApiError ? q.error.message : (q.error as Error)?.message}
              </div>
              <div className="mt-1 text-[9px] text-ink-muted">
                If the endpoint is new to your build, restart the backend — serverless-offline only registers new routes at boot.
              </div>
            </div>
          ) : rows.length === 0 ? (
            <p className="rounded border border-dashed border-surface-border bg-surface-muted/20 p-2 text-[10px] text-ink-muted">
              No templates yet. Click "New template" above to create one — it'll appear here and stay available for every future form.
            </p>
          ) : (
            <div className="space-y-2">
              {grouped.map(([category, templates]) => (
                <div key={category}>
                  <div className="mb-1 text-[9px] uppercase tracking-wider text-ink-muted">
                    {category}
                  </div>
                  <ul className="space-y-1">
                    {templates.map((t) => (
                      <FieldTemplatePaletteRow
                        key={t.field_template_id}
                        template={t}
                        onInsert={onInsert}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editor && (
        <TemplateEditorModal
          state={editor}
          setState={setEditor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            // Refresh the palette immediately so the new template shows up
            // without waiting for the 5s staleTime.
            qc.invalidateQueries({ queryKey: ['field-templates', 'palette'] });
            qc.invalidateQueries({ queryKey: ['field-templates'] });
          }}
        />
      )}
    </div>
  );
}

function FieldTemplatePaletteRow({
  template,
  onInsert,
}: {
  template: FieldTemplateRow;
  onInsert: (fields: DraftField[]) => void;
}) {
  const parsed = useMemo(() => {
    try {
      const raw = JSON.parse(template.template_fields_json);
      if (!Array.isArray(raw)) return null;
      // Coerce to DraftField shape — template_fields_json already uses
      // the same schema, so this is a pass-through with a light guard.
      return raw as DraftField[];
    } catch {
      return null;
    }
  }, [template.template_fields_json]);
  const count = parsed?.length ?? 0;

  return (
    <li
      draggable={count > 0}
      onDragStart={(e) => {
        if (!parsed) return;
        // Payload = the parsed field list. Canvas drop handler reads
        // the same MIME + parses it back into DraftField[].
        e.dataTransfer.setData(
          'application/x-field-template',
          JSON.stringify(parsed),
        );
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className="group rounded border border-surface-border bg-surface-card p-1.5 hover:border-accent-300"
    >
      <div className="flex items-center gap-1.5">
        <GripHorizontal className="h-3 w-3 shrink-0 cursor-grab text-ink-muted opacity-40 group-hover:opacity-100" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-medium text-ink-primary" title={template.description ?? undefined}>
            {template.name}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-[9px] text-ink-muted">
            <span className="font-mono">{template.reference_number}</span>
            <span>· {count} field{count === 1 ? '' : 's'}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => parsed && onInsert(parsed)}
          disabled={count === 0}
          title={count === 0 ? 'Template has no fields' : 'Insert template fields into the form'}
          className="shrink-0 rounded border border-accent-300 bg-accent-50 px-1.5 py-0.5 text-[10px] font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-50"
        >
          Insert
        </button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

function diffFields(
  prevJson: string | null,
  next: DraftField[],
): FieldDiff {
  const prev: DraftField[] = prevJson ? (JSON.parse(prevJson) as DraftField[]) : [];
  const prevByKey = new Map(prev.map((f) => [f.field_key, f]));
  const nextByKey = new Map(next.map((f) => [f.field_key, f]));

  const added = next.filter((f) => !prevByKey.has(f.field_key));
  const removed = prev.filter((f) => !nextByKey.has(f.field_key));
  const changed: FieldDiff['changed'] = [];
  for (const f of next) {
    const p = prevByKey.get(f.field_key);
    if (!p) continue;
    const parts: string[] = [];
    if (p.field_label !== f.field_label) parts.push('label');
    if (p.field_type !== f.field_type) parts.push('type');
    if (JSON.stringify(p.config) !== JSON.stringify(f.config)) parts.push('rules');
    if (p.field_order !== f.field_order) parts.push('order');
    if (parts.length > 0) {
      changed.push({
        field_key: f.field_key,
        field_label: f.field_label,
        summary: parts.join(', '),
      });
    }
  }
  return { added, removed, changed };
}
